import { registerAuditLabels } from "@vixio/ui";
import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo pulso (ver modules/pulso/README.md). */

export interface Team {
  id: string;
  org_id: string;
  name: string;
  client_name: string | null;
  client_ref: string | null;
  manager_person_id: string | null;
  cadence_days: number;
  min_responses: number;
  drop_threshold: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: string;
  team_id: string;
  person_id: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TeamViewer {
  id: string;
  team_id: string;
  user_id: string;
  created_at: string;
}

export interface CycleResult {
  cycle_id: string;
  org_id: string;
  team_id: string;
  team_name: string;
  client_name: string | null;
  number: number;
  starts_on: string;
  ends_on: string;
  status: "open" | "closed";
  invited_count: number;
  response_count: number;
  min_responses: number;
  released: boolean;
  score: number | null;
  items: Record<string, number> | null;
  previous_score: number | null;
  delta: number | null;
  drop_alert: boolean;
}

export interface Alert {
  id: string;
  team_id: string;
  cycle_id: string;
  previous_cycle_id: string;
  previous_score: number;
  current_score: number;
  drop: number;
  created_at: string;
}

export const CYCLE_STATUS: StatusMap = {
  open: { label: "em coleta", tone: "info" },
  closed: { label: "encerrado", tone: "neutral" },
};

export interface PulsoItem {
  id: string;
  text: string;
}

/** Afirmações do instrumento vigente (catálogo global). */
export function usePulsoItems() {
  return useQuery({
    queryKey: ["pulso", "instrument"],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const r = await unwrap<Array<{ definition: { items: PulsoItem[]; scale?: { min: number; max: number; labels?: string[] } } }>>(
        from("pulso", "instrument_versions").select("definition").order("created_at", { ascending: false }).limit(1),
      );
      return r[0]?.definition ?? { items: [] };
    },
  });
}

export const ITEM_SHORT: Record<string, string> = {
  clareza: "Clareza de prioridade",
  carga: "Carga de trabalho",
  reconhecimento: "Reconhecimento",
  colaboracao: "Colaboração",
  seguranca: "Segurança para falar",
};

export function fmtScore(v: number | null | undefined): string {
  return v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function fmtDelta(v: number | null | undefined): string {
  if (v === null || v === undefined) return "-";
  const n = Number(v);
  const s = Math.abs(n).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return n > 0 ? `+${s}` : n < 0 ? `−${s}` : s;
}

/** Pesquisa pública do convite (pulso.get_survey): sem nada que identifique quem responde. */
export interface PublicSurvey {
  organization: string | null;
  team: string;
  cycle: number;
  ends_on: string;
  title: string | null;
  intro: string | null;
  scale: { min: number; max: number; labels: string[] } | null;
  items: Array<{ id: string; text: string }>;
  min_responses: number;
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "pulso.get_survey": { args: { p_token: string }; returns: PublicSurvey };
    "pulso.submit": { args: { p_token: string; p_answers: Record<string, number> }; returns: { ok: boolean; message: string } };
    "pulso.install": { args: { p_org: string }; returns: string };
    "pulso.create_team": {
      args: {
        p_org: string;
        p_name: string;
        p_client_name?: string | null;
        p_manager_person?: string | null;
        p_cadence_days?: number;
        p_min_responses?: number;
        p_drop_threshold?: number;
        p_client_ref?: string | null;
      };
      returns: string;
    };
    "pulso.update_team": {
      args: {
        p_team: string;
        p_name: string | null;
        p_client_name: string | null;
        p_manager_person: string | null;
        p_cadence_days: number | null;
        p_min_responses: number | null;
        p_drop_threshold: number | null;
        p_active: boolean | null;
      };
      returns: null;
    };
    "pulso.set_team_member": { args: { p_team: string; p_person: string; p_active?: boolean }; returns: string };
    "pulso.set_team_viewer": { args: { p_team: string; p_user: string; p_enabled?: boolean }; returns: null };
    "pulso.open_cycle": { args: { p_team: string; p_starts_on?: string | null }; returns: string };
    "pulso.close_cycle": { args: { p_cycle: string }; returns: { cycle_id: string; released: boolean; alert_id: string | null } };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "pulso.installed": "Pulso instalado",
  "pulso.team_created": "Equipe criada",
  "pulso.team_updated": "Equipe atualizada",
  "pulso.team_member_set": "Membro incluído ou alterado",
  "pulso.team_viewer_set": "Leitor do cliente alterado",
  "pulso.cycle_opened": "Ciclo aberto",
  "pulso.cycle_closed": "Ciclo encerrado",
  "pulso.response_recorded": "Resposta anônima recebida",
});
